import { ArrowLeft, X } from 'lucide-react'
import { useRef } from 'react'

import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'

import { COMMAND_PALETTE_HOME_ID } from './context'
import type { CommandPaletteSection } from './types'
import { useCommandPalette } from './use-command-palette'

/** the modal surface. mounted once globally by CommandPaletteProvider. all
 * state (open, active section, search, registered sections) flows from
 * context, so this component is pure rendering.
 */
export function CommandPalette() {
  const {
    isOpen,
    activeId,
    search,
    sections,
    close,
    drillInto,
    back,
    setSearch
  } = useCommandPalette()

  const inputRef = useRef<HTMLInputElement>(null)

  const isHome = activeId === COMMAND_PALETTE_HOME_ID
  const activeSection = isHome
    ? null
    : (sections.find(section => section.id === activeId) ?? null)

  function onOpenChange(next: boolean) {
    if (!next) close()
  }

  /** action sections (onSelect, no Content) run their action and close,
   * everything else drills into its sub-view.
   */
  function selectSection(section: CommandPaletteSection) {
    if (section.onSelect) {
      section.onSelect()
      close()
      return
    }
    drillInto(section.id)
  }

  function onKeyDown(event: React.KeyboardEvent) {
    const isBack = event.key === 'Backspace' || event.key === 'ArrowLeft'
    if (isBack && search === '' && !isHome) {
      event.preventDefault()
      back()
    }
  }

  const title = isHome ? 'Command' : (activeSection?.title ?? 'Command')
  const placeholder = isHome
    ? 'Type a command…'
    : (activeSection?.placeholder ?? 'Search…')

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={onOpenChange}
      title={title}
      description='Search and run commands'
      showCloseButton={false}
      className='top-[20%] sm:max-w-xl'
      onOpenAutoFocus={event => {
        /** radix focuses the first focusable child (the back/close button)
         * by default. steal focus to the search input instead so users
         * can start typing immediately.
         */
        event.preventDefault()
        inputRef.current?.focus()
      }}
    >
      <div className='flex items-center justify-between border-b border-border bg-popover px-4 py-3'>
        <div className='flex items-center gap-2'>
          {!isHome && (
            <button
              type='button'
              aria-label='Back'
              onClick={back}
              className='rounded p-1 text-ink-muted transition-colors hover:bg-cream hover:text-ink'
            >
              <ArrowLeft className='size-4' />
            </button>
          )}
          <h3 className='font-display text-[16px] tracking-[-0.2px] text-ink'>
            {title}
          </h3>
        </div>
        <button
          type='button'
          aria-label='Close'
          onClick={close}
          className='rounded p-1 text-ink-muted transition-colors hover:bg-cream hover:text-ink'
        >
          <X className='size-4' />
        </button>
      </div>

      <Command className='rounded-none bg-popover p-0' onKeyDown={onKeyDown}>
        <CommandInput
          ref={inputRef}
          value={search}
          onValueChange={setSearch}
          placeholder={placeholder}
          className='!h-11 !text-[13px]'
        />
        <CommandList className='!max-h-[480px] !px-0 !py-1'>
          {isHome ? (
            <HomeContent sections={sections} onSelect={selectSection} />
          ) : activeSection?.Content ? (
            <activeSection.Content />
          ) : (
            <div className='px-4 py-12 text-center text-[13px] text-ink-soft'>
              Unknown section
            </div>
          )}
        </CommandList>

        <div className='border-t border-border bg-cream/30 px-4 py-2 text-[11px] text-ink-muted'>
          <span className='font-medium'>↑↓</span> navigate ·{' '}
          <span className='font-medium'>↵</span> select ·{' '}
          {!isHome && (
            <>
              <span className='font-medium'>⌫</span> back ·{' '}
            </>
          )}
          <span className='font-medium'>esc</span> close
        </div>
      </Command>
    </CommandDialog>
  )
}

type HomeContentProps = {
  sections: CommandPaletteSection[]
  onSelect: (section: CommandPaletteSection) => void
}

/** group order priority. anything not listed falls back to alpha ordering
 * after these. keeps 'Pages' first and 'Actions' last regardless of
 * registration order.
 */
const GROUP_ORDER = ['Pages', 'Actions'] as const
const DEFAULT_GROUP = 'Pages'

function HomeContent({ sections, onSelect }: HomeContentProps) {
  if (sections.length === 0) {
    return (
      <div className='px-4 py-12 text-center text-[13px] text-ink-soft'>
        No sections available
      </div>
    )
  }

  const groups = new Map<string, CommandPaletteSection[]>()
  for (const section of sections) {
    const groupName = section.group ?? DEFAULT_GROUP
    const list = groups.get(groupName) ?? []
    list.push(section)
    groups.set(groupName, list)
  }
  const orderedGroupNames = [...groups.keys()].sort((left, right) => {
    const leftRank = GROUP_ORDER.indexOf(left as (typeof GROUP_ORDER)[number])
    const rightRank = GROUP_ORDER.indexOf(right as (typeof GROUP_ORDER)[number])
    if (leftRank !== -1 && rightRank !== -1) return leftRank - rightRank
    if (leftRank !== -1) return -1
    if (rightRank !== -1) return 1
    return left.localeCompare(right)
  })

  return (
    <>
      <CommandEmpty>
        <div className='px-4 py-12 text-center text-[13px] text-ink-soft'>
          No matches
        </div>
      </CommandEmpty>
      {orderedGroupNames.map(groupName => (
        <CommandGroup key={groupName} heading={groupName}>
          {(groups.get(groupName) ?? []).map(section => {
            const Icon = section.icon
            const showBadge = section.badge !== undefined && section.badge > 0
            return (
              <CommandItem
                key={section.id}
                value={section.title}
                onSelect={() => onSelect(section)}
                className='!py-2'
              >
                <Icon className='size-[15px]' />
                <span>{section.title}</span>
                {showBadge && (
                  <span className='ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1.5 text-[10px] font-semibold text-white'>
                    {section.badge! > 99 ? '99+' : section.badge}
                  </span>
                )}
              </CommandItem>
            )
          })}
        </CommandGroup>
      ))}
    </>
  )
}
